import env from "$root/env";
import { eventBus } from "$root/event-bus";
import { isAxiosResponse } from "$root/third-party-integrations/axios";
import { seconds } from "$root/utils";
import { readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { sleep } from "@mdotm/shadcn/legacy/utils";
import { isAxiosError } from "axios";
import { ErrorResponse, type User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { accessToken$, impersonateEmail$ } from "../session/atom";

// `window.location.origin` lets the same build work on localhost, ephemeral preview
// origins and prod — but every origin must be registered in the Cognito app client's
// "Allowed callback URLs" / "Allowed sign-out URLs" (infra prerequisite).
const redirectBase = window.location.origin;

/** A single AWS Cognito user pool / app client the app can authenticate against. */
export type CognitoPool = {
	authority: string;
	clientId: string;
	domain: string;
	/**
	 * Optional Cognito identity-provider name (e.g. `MorganStanley`) for a pool that fronts a
	 * single external IdP. See {@link identityProviderParams} for why it is worth naming.
	 */
	identityProvider?: string;
};

// Default pool — used for every email whose domain is not explicitly mapped below.
const defaultPool: CognitoPool = {
	authority: env.cognitoAuthority,
	clientId: env.cognitoClientId,
	domain: env.cognitoDomain,
};

// Per-email-domain pool overrides (e.g. `morganstanley.com` -> a dedicated pool).
// Keys are email domains; see runtime-envs/*.json. They are re-keyed on lowercase here rather
// than trusted to be written that way, because the key is what a pool is named by in its
// callback URL — a stray capital in the config would otherwise resolve one way on the sign-in
// side (`poolForEmailDomain` lowercases) and another on the way back.
const poolsByEmailDomain = Object.fromEntries(
	Object.entries((env.cognitoPoolsByEmailDomain ?? {}) as Record<string, CognitoPool>).map(([emailDomain, pool]) => [
		emailDomain.trim().toLowerCase(),
		pool,
	]),
);

// Every pool this build can authenticate against.
const configuredPools: CognitoPool[] = [defaultPool, ...Object.values(poolsByEmailDomain)];

// Builds before this one persisted the selected pool here, to reconstruct the right
// `UserManager` on the callback and on renew. The pool now travels in the callback URL and is
// otherwise derived from the OIDC user store (see `activeSession`), so nothing reads this — but
// every browser that ran such a build still holds a record of which authority / client / hosted
// UI that user signed in against. Drop it once, on the way past. Removable once no browser can
// still be carrying one (i.e. comfortably after this ships).
window.localStorage.removeItem("cognito.selectedPool");

/** Same Cognito user pool *and* app client — the pair a `UserManager` and its store are keyed by. */
function isSamePool(a: CognitoPool, b: CognitoPool): boolean {
	return a.authority === b.authority && a.clientId === b.clientId;
}

/** Percent-decode a URL path segment, leaving a malformed one (`decodeURIComponent` throws) as-is. */
function decodeUriSegment(segment: string): string {
	try {
		return decodeURIComponent(segment);
	} catch {
		return segment;
	}
}

/**
 * Resolve the Cognito pool for an email domain: its dedicated pool when the domain is
 * mapped in the runtime env, the default pool otherwise.
 *
 * This is also how the pool named in a callback URL is resolved, and it is why a URL can
 * never widen anything: the domain only ever selects among the pools declared in the env,
 * so a crafted link cannot point the PKCE code exchange — and the `Authorization` header
 * that follows — at an authority the attacker controls. An unmapped domain lands on the
 * default pool, exactly as the same domain would at sign-in.
 */
export function poolForEmailDomain(emailDomain: string): CognitoPool {
	return poolsByEmailDomain[emailDomain.trim().toLowerCase()] ?? defaultPool;
}

/** Resolve the Cognito pool to use for a given email, based on its domain. */
export function poolForEmail(email: string): CognitoPool {
	return poolForEmailDomain(email.split("@")[1] ?? "");
}

/**
 * The email domain a pool is registered under, or `null` for the default pool (which is
 * mapped to no domain in particular — it is the fallback for all of them).
 *
 * Pools are configured as a domain -> pool map and carry no id of their own, so this
 * reverse lookup is what lets a pool name itself in a URL.
 *
 * The default pool short-circuits to `null` before the scan: an env may well map a domain to
 * the very same authority + client id as the default (the honest way to write "acme.com uses
 * the default pool", or a copy-pasted config block), and matching it there would move *every*
 * unmapped user's `redirect_uri` onto that domain's path — an URL nobody registered, so
 * Cognito would reject every sign-in in that environment.
 */
function emailDomainForPool(pool: CognitoPool): string | null {
	if (isSamePool(pool, defaultPool)) {
		return null;
	}
	for (const [emailDomain, candidate] of Object.entries(poolsByEmailDomain)) {
		if (isSamePool(candidate, pool)) {
			return emailDomain;
		}
	}
	return null;
}

/**
 * The OIDC `redirect_uri` for a pool: `/auth/callback/<emailDomain>` for a domain-mapped
 * pool, plain `/auth/callback` for the default one.
 *
 * Encoding the pool in the path makes the callback self-describing — the page no longer
 * depends on this browser having remembered which pool started the sign-in. The default
 * pool deliberately keeps the bare URL so the overwhelmingly common flow needs no new
 * "Allowed callback URL" registered; each *mapped* pool's app client, however, must have
 * `<origin>/auth/callback/<emailDomain>` registered (infra prerequisite) or Cognito
 * rejects the authorize request outright.
 *
 * The value is derived from the pool itself rather than passed in, so the authorize
 * request and the token exchange that follows always send the same `redirect_uri` —
 * Cognito compares them and rejects a mismatch.
 */
export function redirectUriForPool(pool: CognitoPool): string {
	const emailDomain = emailDomainForPool(pool);
	return `${redirectBase}/auth/callback${emailDomain ? `/${encodeURIComponent(emailDomain)}` : ""}`;
}

/**
 * The pool holding this session's OIDC user, and that user, or `null` when no configured
 * pool has one (nobody is signed in here).
 *
 * There is no separate record of "which pool started the sign-in": the pool is encoded in
 * the callback URL for the exchange ({@link redirectUriForPool}) and, from then on, it is
 * simply the pool whose `userStore` holds a user. Deriving it removes a second source of
 * truth that could disagree with the store it was supposed to describe — a stale or
 * hand-cleared pointer used to strand a perfectly good session.
 *
 * The scan is one `localStorage` read per configured pool (a single one in every
 * environment with no domain overrides), and it is what every post-callback caller needs:
 * boot, silent renew and logout all ask "which session is this?", not "which pool did the
 * user type an email for?".
 */
async function activeSession(): Promise<{ pool: CognitoPool; user: User } | null> {
	let newest: { pool: CognitoPool; user: User } | null = null;
	for (const pool of configuredPools) {
		const user = await userManagerFor(pool).getUser();
		// Two pools *should* never both hold a user — `completeSignin` clears the others — but
		// builds before this one never did, so a browser can arrive here with a leftover from an
		// earlier sign-in against a different pool, disambiguated only by the pointer this code
		// no longer keeps. Take the most recently issued session (`expires_at` is issue time plus
		// a fixed TTL, so the freshest expiry is the freshest sign-in) instead of the first hit:
		// picking positionally would let a lapsed session shadow the live one indefinitely, since
		// a failed renew deliberately leaves the stored user in place.
		if (user && (!newest || (user.expires_at ?? 0) > (newest.user.expires_at ?? 0))) {
			newest = { pool, user };
		}
	}
	return newest;
}

/**
 * The `localStorage` key oidc-client-ts keeps a pool's stored `User` under.
 *
 * `UserManager` names the entry `user:{authority}:{client_id}` and `WebStorageStateStore`
 * prefixes it with `oidc.` — the default `userManagerFor` keeps. Reconstructed here rather than
 * read back from the library, because a cross-tab `storage` event hands us nothing but a key:
 * this is what decides whether that key was a session or some unrelated preference.
 */
function userStoreKeyFor(pool: CognitoPool): string {
	return `oidc.user:${pool.authority}:${pool.clientId}`;
}

/**
 * What another tab of this browser did to the shared OIDC session. The `storage` event carries
 * the entry before and after, which is enough to tell a renew (same subject, fresh tokens —
 * nothing to re-resolve) from a sign-in as somebody else (everything to re-resolve).
 */
export type StoredSessionChange =
	/** A pool that held no user now holds one: somebody signed in. */
	| { kind: "signed-in" }
	/** A pool's stored user is a *different* subject than the one it replaced. */
	| { kind: "switched" }
	/** Same subject, new tokens: a silent renew. */
	| { kind: "renewed" }
	/** A pool's stored user is gone: a sign-out, or a renew that gave up. */
	| { kind: "signed-out" };

/** The `sub` a stored `User` was issued for, or `null` for an absent or unreadable entry. */
function subjectOfStoredUser(storageString: string | null): string | null {
	if (!storageString) {
		return null;
	}
	try {
		return (JSON.parse(storageString) as { profile?: { sub?: string } }).profile?.sub ?? null;
	} catch {
		return null;
	}
}

/**
 * Watch the OIDC user store for changes made by **other tabs** of this browser, and return the
 * unsubscribe.
 *
 * The store is the one thing every tab genuinely shares — `accessToken$` is per-tab and in
 * memory, and `user$` is deliberately not mirrored (see its own note). So this is where a tab
 * learns that the browser signed in, renewed or signed out somewhere else; the identity that
 * follows is then resolved locally, through the same hydration a sign-in uses, never copied
 * from the tab that made the change.
 *
 * `storage` does not fire in the tab that wrote, so a handler here can act on the store without
 * looping back on itself.
 */
export function subscribeToStoredSessionChanges(handler: (change: StoredSessionChange) => void): () => void {
	const sessionKeys = new Set(configuredPools.map(userStoreKeyFor));
	const onStorage = (event: StorageEvent) => {
		if (event.storageArea !== window.localStorage || event.key == null || !sessionKeys.has(event.key)) {
			return;
		}
		// Presence is read off the raw entries and only the both-present case looks at subjects:
		// an entry we cannot parse is still an entry, and must not be mistaken for a sign-out.
		if (event.newValue == null) {
			handler({ kind: "signed-out" });
		} else if (event.oldValue == null) {
			handler({ kind: "signed-in" });
		} else if (subjectOfStoredUser(event.oldValue) !== subjectOfStoredUser(event.newValue)) {
			handler({ kind: "switched" });
		} else {
			handler({ kind: "renewed" });
		}
	};
	window.addEventListener("storage", onStorage);
	return () => window.removeEventListener("storage", onStorage);
}

/**
 * The token forwarded to the MDOTM backend. We use the Cognito **access token**
 * because the existing axios interceptor sets `Authorization: Bearer <accessToken$>`
 * and the backend resolves the user via `GET /user/info`. Switch to `user.id_token`
 * here if the backend expects the id token instead.
 */
export function tokenFromOidcUser(user: User | null): string | null {
	return user?.access_token ?? null;
}

// One `UserManager` per pool, created lazily and cached so events stay wired once.
const managerCache = new Map<string, UserManager>();

function userManagerFor(pool: CognitoPool): UserManager {
	const key = `${pool.authority}|${pool.clientId}`;
	const cached = managerCache.get(key);
	if (cached) {
		return cached;
	}
	const manager = new UserManager({
		authority: pool.authority,
		client_id: pool.clientId,
		redirect_uri: redirectUriForPool(pool),
		response_type: "code", // Authorization Code + PKCE
		// A pool with an external IdP can't carry `aws.cognito.signin.user.admin`: a federated authorize rejects
		// it (invalid_scope), and federated users have no Cognito self-service API to use it with anyway.
		scope: pool.identityProvider ? "openid profile email" : "openid profile email aws.cognito.signin.user.admin",
		userStore: new WebStorageStateStore({ store: window.localStorage }),
		automaticSilentRenew: true, // refresh_token grant (no iframe), Cognito issues refresh tokens
		monitorSession: false, // Cognito has no usable check_session iframe
		revokeTokenTypes: ["refresh_token"], // Cognito cannot revoke access tokens
	});

	// Keep accessToken$ in sync on (re)load and on silent renew.
	manager.events.addUserLoaded((user) => {
		writeAtom(accessToken$, tokenFromOidcUser(user));
	});
	manager.events.addUserUnloaded(() => {
		writeAtom(accessToken$, null);
	});
	manager.events.addSilentRenewError((err) => {
		console.warn("OIDC silent renew failed", err);
		// `SilentRenewService` only ever retries its own timeouts, and it schedules the next
		// renew off the `accessTokenExpiring` timer — which is already spent by the time this
		// fires. So a single failed scheduled renew (the classic one: waking from sleep before
		// the network is back) would otherwise leave the token to expire untouched, and the
		// session's survival would rest on whatever request happens to 401 next. Retry here
		// instead, with the same backoff and error classification as the 401 path — and report
		// a verdict of "unrecoverable" straight away rather than waiting for a request to
		// rediscover it.
		void coordinatedRenew().then((outcome) => {
			if (outcome.status === "give-up") {
				eventBus.emit("unauthorized");
			}
		});
	});

	managerCache.set(key, manager);
	return manager;
}

/**
 * The signed-in user for this session, renewing first when the stored access token has
 * already expired.
 *
 * `User.expired` reflects the **access** token only, and its lifetime (typically an hour) is
 * far shorter than the refresh token stored next to it. Returning `null` on `expired` would
 * therefore cap every session at the access-token TTL the moment the app is not running:
 * `SilentRenewService` renews off the `accessTokenExpiring` timer, and that timer is cancelled
 * outright for a token that is already past expiry (see `AccessTokenEvents.load`), so a reload,
 * a new tab or a browser restart after the TTL would face an interactive sign-in with a
 * perfectly good refresh token sitting in storage. Spend that refresh token instead.
 */
export async function getValidOidcUser(): Promise<User | null> {
	const session = await activeSession();
	if (!session) {
		return null;
	}
	const { user } = session;
	if (!user.expired) {
		return user;
	}
	const outcome = await coordinatedRenew();
	// A `postponed` renew collapses to "not signed in" here like any other failure: boot has
	// nothing to hydrate a session *with*. It is still not a logout — the stored OIDC user and
	// its refresh token are left untouched, so once the network is back a reload signs the user
	// straight back in without an interactive round trip.
	return outcome.status === "renewed" ? outcome.user : null;
}

/**
 * The `identity_provider` authorize parameter for a pool that declares one, or nothing at all.
 *
 * A pool dedicated to one corporate IdP still opens its Hosted UI on a provider picker with a
 * single entry; naming the provider skips it and lands the user on their own IdP. The param is
 * omitted — rather than sent empty — for every other pool: an empty `identity_provider` is not
 * the same as none, and Cognito would answer the authorize request with an error instead of
 * showing the Hosted UI.
 */
function identityProviderParams(pool: CognitoPool): { extraQueryParams?: Record<string, string> } {
	return pool.identityProvider ? { extraQueryParams: { identity_provider: pool.identityProvider } } : {};
}

/**
 * Start the Cognito sign-in for the given email: pick the pool from the email domain and
 * redirect to that pool's Hosted UI. Nothing has to be stashed for the callback — the pool
 * travels in the `redirect_uri` (see {@link redirectUriForPool}).
 */
export function beginSignin(email: string, from?: string): Promise<void> {
	const pool = poolForEmail(email);
	// A fresh interactive sign-in must never inherit a leftover impersonation.
	writeAtom(impersonateEmail$, null);
	return userManagerFor(pool).signinRedirect({
		state: { from: from ?? "/" },
		login_hint: email, // pre-fill the email on the Cognito Hosted UI
		...identityProviderParams(pool),
	});
}

/**
 * Start the Cognito sign-in for an email *domain* — the entry point behind `/sso/:emailDomain`,
 * for a partner portal that deep-links its own users into the app.
 *
 * Such a portal would otherwise link straight at Cognito with an authorize URL it built itself,
 * and a hand-built one carries neither `state` nor `code_challenge`: the callback then has no
 * stored state entry to recover the PKCE verifier from, so `signinRedirectCallback` throws and
 * the user is bounced to /login. Owning the authorize request here is what keeps state, PKCE,
 * the scopes and the pool's `redirect_uri` consistent with the rest of the app; the domain in
 * the link only ever *selects* among the configured pools (see {@link poolForEmailDomain}).
 *
 * No `login_hint`: a domain-only link knows no email.
 */
export function beginSigninForEmailDomain(emailDomain: string, from?: string): Promise<void> {
	const pool = poolForEmailDomain(emailDomain);
	// A fresh interactive sign-in must never inherit a leftover impersonation.
	writeAtom(impersonateEmail$, null);
	return userManagerFor(pool).signinRedirect({
		state: { from: from ?? "/" },
		...identityProviderParams(pool),
	});
}

/**
 * Complete the Cognito code exchange on the callback route. `emailDomain` is the optional
 * `/auth/callback/:emailDomain` segment and fully determines the pool: present means that
 * domain's pool, absent means the default one — which is precisely what
 * {@link redirectUriForPool} encoded when the sign-in started. Nothing is read back from
 * storage, so a browser that never signed in here (or had its storage cleared mid-flow)
 * completes the exchange just the same.
 *
 * The segment arrives exactly as it sits in the URL — neither `new URL().pathname` nor the
 * route's params serde decodes it — so it is decoded here, mirroring the `encodeURIComponent`
 * on the way out.
 */
export async function completeSignin(emailDomain?: string | null): Promise<{ token: string; from: string }> {
	const pool = poolForEmailDomain(decodeUriSegment(emailDomain ?? ""));
	// The session about to be established must be the only one: `activeSession` would otherwise
	// have to choose between it and a leftover from an earlier sign-in against a *different*
	// pool. Cleared before the exchange, not after — `removeUser` emits `userUnloaded`, which
	// nulls `accessToken$`, and after the exchange that would wipe the token just installed.
	await Promise.all(
		configuredPools
			.filter((candidate) => !isSamePool(candidate, pool))
			.map((candidate) => userManagerFor(candidate).removeUser()),
	);
	const user = await userManagerFor(pool).signinRedirectCallback();
	const token = tokenFromOidcUser(user);
	if (!token) {
		throw new Error("OIDC callback returned no token");
	}
	const from = (user.state as { from?: string } | undefined)?.from || "/";
	return { token, from };
}

/**
 * What went wrong on the auth callback, at the granularity a *user* can describe out loud.
 *
 * The three failures below are indistinguishable from the outside — all of them end the
 * sign-in — yet each is a different defect on a different side of the exchange. Naming them
 * is what lets a report ("it said my account isn't enabled") land on the right one without
 * devtools, a support round trip, or credentials we do not have.
 */
export type AuthCallbackFailure = "sso-protocol" | "not-provisioned" | "network" | "unknown";

// oidc-client-ts reports a callback it cannot even begin to process by message, not by type:
// both mean the response carries no state entry this browser can pair with a stored PKCE
// verifier. The classic cause is an authorize URL somebody built by hand (a partner portal
// deep-linking at Cognito) — see `beginSigninForEmailDomain` for the supported way in.
const OIDC_STATE_ERROR_MESSAGES = ["No state in response", "No matching state found in storage"];

/** The HTTP status of a failed API call, or `undefined` when the failure never got a response. */
export function httpStatusFromError(err: unknown): number | undefined {
	const response = err != null && typeof err === "object" ? (err as { response?: unknown }).response : null;
	return isAxiosResponse(response) ? response.status : undefined;
}

/**
 * Classify what failed between the Cognito redirect and a hydrated session, i.e. any error
 * thrown by {@link completeSignin} or by the identity fetch that follows it.
 */
export function classifyAuthCallbackError(err: unknown): AuthCallbackFailure {
	// The token endpoint answered with an OAuth error, so the protocol round trip is what
	// failed — nothing was ever exchanged and there is no session to speak of. Distinct from
	// the terminal *renew* errors above, which judge a refresh token we already hold.
	if (err instanceof ErrorResponse) {
		return "sso-protocol";
	}
	if (err instanceof Error && OIDC_STATE_ERROR_MESSAGES.some((message) => err.message.includes(message))) {
		return "sso-protocol";
	}
	// The exchange succeeded and the backend still refused the identity: the token is valid,
	// this user just isn't resolvable on our side (e.g. a federated user whose Cognito
	// username is not their email). Retrying or signing in again cannot fix it — only
	// provisioning can — which is why it must not read as a generic error.
	const status = httpStatusFromError(err);
	if (status === 401 || status === 403) {
		return "not-provisioned";
	}
	// A request that never got a verdict says nothing about either the protocol or the
	// account: `fetch` rejecting with a `TypeError` (oidc-client-ts's transport) and an axios
	// error with no response are the same "we never heard back".
	if (isAxiosError(err) && err.response == null) {
		return "network";
	}
	if (err instanceof TypeError && err.message.includes("Failed to fetch")) {
		return "network";
	}
	return "unknown";
}

/**
 * Attempt an OIDC silent renew (refresh_token grant) on the active pool. Resolves to `null`
 * when there is nothing to renew, and rejects when the renew itself failed — the caller has to
 * tell those apart (see {@link isTerminalRenewError}).
 */
export async function silentRenew(): Promise<User | null> {
	const session = await activeSession();
	if (!session?.user.refresh_token) {
		// No session at all, or one without a refresh token. In the latter case `signinSilent`
		// would fall back to a hidden-iframe `prompt=none` flow, which this app never configures
		// (`silent_redirect_uri`) and Cognito cannot serve anyway. Report "nothing to renew"
		// instead of letting it throw a misleading configuration error that would read as a
		// failed — hence retryable — renew.
		return null;
	}
	return userManagerFor(session.pool).signinSilent();
}

/**
 * Outcome of {@link coordinatedRenew}. `renewed` carries the fresh token (already installed in
 * `accessToken$`); `postponed` means the renew could not even be attempted for a reason that
 * says nothing about the session's validity, so the caller must keep the session; `give-up`
 * means the session is unrecoverable and the caller should log out.
 */
export type RenewOutcome =
	| { status: "renewed"; token: string; user: User }
	| { status: "postponed"; reason: "offline" | "unexpected" }
	| { status: "give-up"; reason: "no-session" | "renew-failed" | "token-rejected" };

// A failed renew has two very different meanings, and conflating them is what makes a session
// feel fragile:
//
//  * the refresh token is genuinely dead — revoked, expired, or issued to another client. No
//    amount of retrying helps; only an interactive sign-in does. The IdP says so explicitly,
//    with an OAuth error code.
//  * the request never got a verdict — laptop wake before Wi-Fi is back, VPN reconnect,
//    captive portal, a Cognito 5xx. The session is intact and the refresh token still has hours
//    on it; logging out here throws away a working session over a network blip.
//
// Only the first list is treated as terminal. Anything else — including OAuth's own transient
// codes (`server_error`, `temporarily_unavailable`) — is retried.
const TERMINAL_OAUTH_ERRORS = new Set(["invalid_grant", "invalid_client", "unauthorized_client", "invalid_scope"]);

/** `true` when the identity provider explicitly rejected the refresh token. */
export function isTerminalRenewError(err: unknown): boolean {
	return err instanceof ErrorResponse && err.error != null && TERMINAL_OAUTH_ERRORS.has(err.error);
}

// Backoff between renew attempts; its length is also the retry count. Kept short on purpose:
// every 401 in the app de-duplicates onto the renew currently in flight, and the boot path
// blocks the loading screen on it, so this is a latency budget as much as a resilience one.
// Each attempt is separately bounded by `silentRequestTimeoutInSeconds` (10s by default in
// oidc-client-ts), which puts the worst case — a network that accepts connections and then
// answers nothing — at roughly half a minute rather than indefinitely.
const RENEW_RETRY_DELAYS_MS = [seconds(1), seconds(3)];

function isOffline(): boolean {
	// `navigator.onLine` is only trustworthy when it says `false` (a `true` can still mean a
	// captive portal), which is exactly the direction used here.
	return navigator.onLine === false;
}

// A 401 does not always mean "token expired". If the backend rejects the token for a
// reason a fresh one won't fix (e.g. it validates the id token but we send the access
// token — see `tokenFromOidcUser`), then `silentRenew` keeps *succeeding* while every
// request keeps 401ing: renew → new token → 401 → renew → ... forever, hammering
// Cognito. `coordinatedRenew` bounds this with two mechanisms:
//
//  1. De-dup — concurrent `unauthorized` events (a dashboard fires many parallel
//     queries) collapse onto one in-flight renew instead of one renew each.
//  2. Circuit breaker — more than MAX renews inside a rolling window means the freshly
//     issued token is being rejected, not merely expired; stop renewing and report
//     `token-rejected` so the caller logs out instead of spinning.
//
// The window auto-resets after quiet time, so a normal hourly expiry (one renew, then
// silence) never trips it; only a tight renew↔401 loop does. The 401 handler only renews for
// requests that carried the *current* token (see `auth-interceptors`), so stragglers holding a
// pre-renew token never reach here and a successful renew costs exactly one count.
const RENEW_BURST_WINDOW_MS = 15_000;
const MAX_RENEWS_IN_WINDOW = 3;

let inFlightRenew: Promise<RenewOutcome> | null = null;
let renewWindowStart = 0;
let renewCountInWindow = 0;

/** One renew, retrying anything that isn't an explicit rejection by the identity provider. */
async function attemptRenewWithRetry(): Promise<RenewOutcome> {
	for (let attempt = 0; ; attempt++) {
		try {
			const user = await silentRenew();
			if (!user) {
				return { status: "give-up", reason: "no-session" };
			}
			const token = tokenFromOidcUser(user);
			if (!token) {
				// Renew resolved a user but no usable token — treat as a failed renew rather
				// than writing `accessToken$ = null`, which would leave `user$.signedIn` true
				// with no token (a zombie session that just 401s until the breaker trips).
				return { status: "give-up", reason: "renew-failed" };
			}
			return { status: "renewed", token, user };
		} catch (err) {
			if (isTerminalRenewError(err)) {
				console.warn("OIDC renew rejected by the identity provider", err);
				return { status: "give-up", reason: "renew-failed" };
			}
			if (isOffline()) {
				// Nothing to retry against, and a dead network is no evidence the session is
				// dead: hold on to it and let the next request retry once we're back.
				console.warn("OIDC renew skipped: browser is offline", err);
				return { status: "postponed", reason: "offline" };
			}
			if (attempt >= RENEW_RETRY_DELAYS_MS.length) {
				console.warn("OIDC renew failed after retries", err);
				return { status: "give-up", reason: "renew-failed" };
			}
			await sleep(RENEW_RETRY_DELAYS_MS[attempt]);
		}
	}
}

async function runCoordinatedRenew(): Promise<RenewOutcome> {
	const now = Date.now();
	if (now - renewWindowStart > RENEW_BURST_WINDOW_MS) {
		renewWindowStart = now;
		renewCountInWindow = 0;
	}
	renewCountInWindow += 1;
	if (renewCountInWindow > MAX_RENEWS_IN_WINDOW) {
		// The refresh keeps producing tokens the backend rejects — renewing again is
		// pointless. Reset so a later, legitimate session starts from a clean slate.
		renewWindowStart = 0;
		renewCountInWindow = 0;
		return { status: "give-up", reason: "token-rejected" };
	}
	const outcome = await attemptRenewWithRetry();
	if (outcome.status === "renewed") {
		// `addUserLoaded` already mirrors the renewed token into `accessToken$`; do it here too
		// so installing the token is not an implicit side effect of an event listener. Writing
		// the value it already holds is a no-op (`atomWithStringStorage` bails on equality), so
		// this cannot churn the observers that restart the server-event stream.
		if (readAtom(accessToken$) !== outcome.token) {
			writeAtom(accessToken$, outcome.token);
		}
	}
	return outcome;
}

/**
 * De-duplicated, loop-safe wrapper around {@link silentRenew} for the 401 handler.
 * Never rejects — always resolves to a {@link RenewOutcome}. See the notes above for
 * why the plain `silentRenew` is unsafe to call directly on every `unauthorized`.
 */
export function coordinatedRenew(): Promise<RenewOutcome> {
	if (inFlightRenew) {
		return inFlightRenew;
	}
	inFlightRenew = runCoordinatedRenew()
		.catch((err): RenewOutcome => {
			// "Never rejects" is load-bearing — the 401 interceptor awaits this bare, and a
			// rejection here would surface in place of the caller's `AxiosError`. Nothing above
			// is expected to throw (the storage-backed atoms could, on a full or locked-down
			// `localStorage`), and if it does that says nothing about the session: keep it.
			console.warn("OIDC renew coordination threw", err);
			return { status: "postponed", reason: "unexpected" };
		})
		.finally(() => {
			inFlightRenew = null;
		});
	return inFlightRenew;
}

export async function beginSignout(): Promise<void> {
	const session = await activeSession();
	const logoutUri = `${window.location.origin}/login`;
	if (!session) {
		// No Cognito session — there is nothing to revoke at the Hosted UI, so just land
		// back on the local login page instead of bouncing the browser to some (necessarily
		// guessed) pool's `/logout` endpoint.
		window.location.href = logoutUri;
		return;
	}
	const { pool } = session;
	await userManagerFor(pool).removeUser();
	// Cognito does not expose `end_session_endpoint` in its discovery document, so
	// logout is a manual redirect to the Hosted UI `/logout` endpoint.
	window.location.href =
		`${pool.domain}/logout?client_id=${encodeURIComponent(pool.clientId)}` +
		`&logout_uri=${encodeURIComponent(logoutUri)}`;
}
